import { formatTranslation } from 'intl';
import { ExtendedFieldTranslationKeys, IssueTranslationKeys } from 'intl/keys';
import { CardContent, Divider } from '@mui/material';
import { MemoIssueStage } from 'components/general/IssueStage';
import { IssueStageSize } from 'components/general/IssueStage/types';
import { MemoIssueCardChip } from 'components/inspection/IssueCardPreview/Chip';
import { ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  DateFieldValueGrid,
  DelayValuesGrid,
  ExpandableField,
  StaticField,
  TextValueField,
} from './fields';
import { ArrowDotted, ArrowSolid } from './icons';
import { useStyles } from './styles';

export interface IssueCardDetailsProps {
  hashtag: string;
  company: string;
  companyFieldLabel: string;
  plannedDatesLabel: string | undefined;
  plannedDates: (string | undefined | null)[] | undefined;
  completionDateLabel: string | undefined;
  completionDates: (string | undefined | null)[] | undefined;
  delayLabel: string | undefined;
  completionAmount: string;
  delays: (number | null)[];
  deleted: boolean;
  stageId: string;
  workflowId: string;
  lastEditString: string;
  isSelected?: boolean;
}

/**
 * Hashtag + last edit, company, planned / completion dates, delay and the
 * completion + stage footer. Collapsed fields animate in when `isExpanded`.
 */
export function IssueCardDetails({
  hashtag,
  company,
  companyFieldLabel,
  plannedDatesLabel,
  plannedDates,
  completionDateLabel,
  completionDates,
  delayLabel,
  completionAmount,
  delays,
  deleted,
  stageId,
  workflowId,
  lastEditString,
  isSelected,
  isExpanded,
}: IssueCardDetailsProps & { isExpanded: boolean }): ReactElement {
  const cs = useStyles();
  const intl = useIntl();
  const arrowDotted = useCallback(
    () => <ArrowDotted htmlColor='#979797' className={cs.cardDateArrow} />,
    [cs]
  );

  const hasPlannedDatesValue = useMemo(() => {
    return Boolean((plannedDates || []).filter((x) => x).length);
  }, [plannedDates]);

  return (
    <>
      <CardContent
        className={(deleted && cs.deleted) || ''}
        style={{
          padding: '10px',
          paddingTop: '0px',
          fontSize: '0.75rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
            gap: '0.25rem',
          }}
        >
          <MemoIssueCardChip
            key={hashtag}
            values={[
              isSelected
                ? {
                    value: `#${hashtag}`,
                    color: '#FFFFFF',
                    textColor: '#000000',
                    border: '1px solid rgb(142, 36, 170)',
                  }
                : `#${hashtag}`,
            ]}
          />
          <span style={{ color: '#757575' }}>{lastEditString}</span>
        </div>
        <div
          style={{
            display: 'grid',
            justifyContent: 'stretch',
            flexDirection: 'column',
            alignItems: 'flex-start',
            width: '100%',
          }}
        >
          <StaticField
            label={companyFieldLabel}
            isExpanded={isExpanded}
            hasValue={Boolean(company)}
            data-qa='static-field'
          >
            <TextValueField
              data-qa='company-name'
              text={company}
              isExpanded={isExpanded}
              tooltipLabel={companyFieldLabel}
            />
          </StaticField>
          <StaticField
            label={plannedDatesLabel}
            isExpanded={isExpanded}
            hasValue={hasPlannedDatesValue}
          >
            <DateFieldValueGrid
              dates={plannedDates || []}
              delays={delays}
              isExpanded={isExpanded}
              Arrow={arrowDotted}
              startDateFieldLabel={formatTranslation(intl, ExtendedFieldTranslationKeys.targetStartDate,)}
              endDateFieldLabel={formatTranslation(intl, ExtendedFieldTranslationKeys.targetCompletionDate,)}
              startDatesDelayIconLabel={formatTranslation(intl, IssueTranslationKeys.startDateDelayLabel,)}
              endDatesDelayIconLabel={formatTranslation(intl, IssueTranslationKeys.delayLabel,)}
            />
          </StaticField>
          <ExpandableField
            label={completionDateLabel}
            isExpanded={isExpanded}
          >
            <DateFieldValueGrid
              dates={completionDates || []}
              delays={[]}
              isExpanded={isExpanded}
              Arrow={() => (
                <ArrowSolid
                  htmlColor='#979797'
                  className={cs.cardDateArrow}
                />
              )}
              startDateFieldLabel={formatTranslation(intl, ExtendedFieldTranslationKeys.finalStartDate,)}
              endDateFieldLabel={formatTranslation(intl, ExtendedFieldTranslationKeys.finalCompletionDate,)}
              startDatesDelayIconLabel={formatTranslation(intl, IssueTranslationKeys.startDateDelayLabel,)}
              endDatesDelayIconLabel={formatTranslation(intl, IssueTranslationKeys.delayLabel,)}
            />
          </ExpandableField>
          <ExpandableField label={delayLabel} isExpanded={isExpanded}>
            <DelayValuesGrid isExpanded={isExpanded} delays={delays} />
          </ExpandableField>
        </div>
      </CardContent>
      <Divider
        style={{
          borderBottomWidth: 'none',
          backgroundColor: 'unset',
        }}
      />
      <CardContent
        className={(deleted && cs.deleted) || ''}
        style={{
          padding: '10px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span className={cs.completionField}>{completionAmount}</span>
        <MemoIssueStage
          stageId={stageId}
          workflowId={workflowId}
          issueArchived={deleted}
          size={IssueStageSize.issueCard}
        />
      </CardContent>
    </>
  );
}
